import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {FaEraser, FaPrint, FaSearch} from "react-icons/fa";
import SelectComponent from "../../../components/select/select-component.jsx";
import Button from "../../../components/button/button.jsx";
import {Card} from "../../../components/card/card.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import {useQuery} from "@tanstack/react-query";
import myUserService from "../../../services/web/my-user-service.js";
import projectUtils from "../../../utils/project-utils.js";
import Alert from "../../../components/alert/alert.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import CortexRenavamContent from "./components/cortex-renavam-content.jsx";
import HeaderTemplateButton from "../../../components/button/header-template-button.jsx";
import {MaskDefault} from "../../../utils/mask.js";
import {useSearchParams} from "react-router";
import cortexService from "../../../services/web/cortex-service.js";

const initialState = {
    type: "",
    value: ""
}

const CortexRenavam = () => {
    const [searchParams] = useSearchParams();

    const [mask, setMask] = useState(null);
    const [state, setState] = useState(initialState);
    const [filters, setFilters] = useState(undefined);

    const [isLoading, setIsLoading] = useState(false);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [data, setData] = useState(undefined);

    const printRef = useRef(undefined);

    const {data: user, isSuccess, isLoading: isLoadingFetch} = useQuery({
        queryFn: () => myUserService.get(),
        queryKey: ["user-logged"]
    });

    const types = useMemo(() => [
        {value: "tenant", label: "Arrendatário"},
        {value: "chassi", label: "Chassi"},
        {value: "pocessor-cpf", label: "CPF Possuidor"},
        {value: "owner-cpf", label: "CPF Proprietário"},
        {value: "engine", label: "Motor"},
        {value: "plate", label: "Placa"},
        {value: "renavam", label: "RENAVAM"}
    ], []);

    useEffect(() => {
        if (!isSuccess) return;

        const field = searchParams.get("field");
        const value = searchParams.get("value");

        if (field && value) {
            const type = types.find(t => t.value === field);
            handleSelected(type, false);

            setState({ type, value });
            apply({ type, value });
        }
    }, [isSuccess, searchParams]);

    const apply = useCallback((filters = undefined) => {
        setData(undefined);
        setFilters(undefined);

        const type = filters?.type || state.type;
        const value = filters?.value || state.value;

        if (!type) {
            projectUtils.swalErrorMessage("", "O campo \"Tipo\" é obrigatório.");
            return;
        }

        if (!value) {
            projectUtils.swalErrorMessage("", "O campo \"Valor\" é obrigatório.");
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append("field", type?.value);
        formData.append("value", value);

        cortexService.renavamSearch(formData)
            .then(({data}) => {
                setFilters({ type, value });
                setData(data)
            })
            .catch(error => {
                console.error(error);
                let message = 'Erro ao realizar pesquisa de passagens.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage("", message);
            })
            .finally(() => setIsLoading(false));
    }, [state]);

    const clear = useCallback(() => {
        setState(initialState);
        setFilters(undefined)
        setData(undefined);
    }, []);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const handleSelected = (selected, set = true) => {
        if (set) setState(prev => ({...prev, type: selected}));

        if (!selected?.value) {
            setMask(null);
            return;
        }

        switch (selected?.value) {
            case 'plate':
                setMask(MaskDefault.plate);
                break
            case 'pocessor-cpf':
            case 'owner-cpf':
                setMask(MaskDefault.cpf);
                break;
            case 'renavam':
                setMask(MaskDefault.renavam);
                break;
            default:
                setMask(null);
        }
    }

    const hasResult = !!data && !Array.isArray(data);

    return (
        <Template isLoading={isLoadingFetch || isLoading}>
            <Template.Header>
                <Template.Title>RENAVAM</Template.Title>

                {
                    hasResult &&
                    <div className={"flex gap-2"}>
                        <Button title="Imprimir" onClick={() => printRef?.current.print()}>
                            <FaPrint />
                        </Button>
                        <HeaderTemplateButton selected={headerSelected} setSelected={setHeaderSelected}/>
                    </div>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 gap-4"}>
                        <SentryRow className={"!col-span-6"}>
                            <SelectComponent
                                id={"type"}
                                value={state.type}
                                options={types}
                                label={"Tipo *"}
                                classNameWrapper={"col-span-6"}
                                onChange={handleSelected}
                            />

                            <div className="col-span-6 flex justify-between gap-4">
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id={"value"}
                                    label={"Valor *"}
                                    value={state.value}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, value: value}))}
                                    mask={mask}
                                />

                                <div className={"flex gap-2"}>
                                    <Button target={"default"} title={"Limpar"} onClick={clear}>
                                        <FaEraser />
                                    </Button>
                                    <Button title={"Buscar"} onClick={() => apply()}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </SentryRow>
                        <section className={"col-span-6 text-center text-[12px] font-bold italic"}>
                            {
                                user?.cpf ?
                                <span className={"text-[12px]"}>* Por exigência do Ministério da Justiça, as consultas efetuadas<br/>durante essa sessão serão vinculadas ao CPF do usuário ativo: <br/>{projectUtils.formatCpfToView(user?.cpf)}</span>
                                : <span className="text-[#ed5565]">* O agente atual não possui CPF cadastrado.</span>
                            }
                        </section>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        {!filters || !hasResult ? (
                            <Alert className="text-center font-bold text-[14px]!" target="info">
                                {!filters
                                    ? "Aplique os filtros para realizar a pesquisa."
                                    : "Nenhum resultado encontrado na pesquisa."}
                            </Alert>
                        ) : (
                            <CortexRenavamContent
                                printRef={printRef}
                                headerSelected={headerSelected}
                                data={data}
                                filters={filters}
                            />
                        )}
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CortexRenavam;